<template>
  <SettingModal :baseSettingsItem="baseSettingsItem">
    <template #content="{ hide }">
      <div>
        <b-overlay class="tw-flex tw-flex-col" :show="loading">
          <div class="tw-ml-auto tw-inline-flex tw-gap-1">
            <button
              :disabled="loading"
              class="bttn bttn--blue tw-w-auto !tw-px-3"
              @click="fetchPromoCodes()"
            >
              <b-icon-arrow-clockwise />
              <span class="tw-sr-only" aria-hidden="true">
                {{ $t("account.reload_promo_codes", "Reload Promo Codes") }}
              </span>
            </button>
            <button class="bttn bttn--blue" @click="showModal">
              {{ $t("account.generate_promo_codes", "Generate Promo Codes") }}
            </button>
          </div>

          <div class="promocode-wrap" v-if="promoCodeList.length">
            <div class="promocode-row" v-for="(code, index) in promoCodeList" :key="index">
              <div class="promocode-row__code">
                <span>
                  {{ code.attributes.code }}
                </span>
                <button
                  type="button"
                  class="bttn-round btn-copy-promocode"
                  name="button"
                  @click="copy(code)"
                >
                  <b-icon-clipboard-data color="currentColor" />
                </button>
              </div>

              <div class="promocode-row__discount">{{ code.attributes.discount }}%</div>

              <div class="promocode-row__status">
                <span
                  :class="{
                    'text-success': !code.attributes.used && !code.attributes.reusable,
                    'text-danger': code.attributes.used && !code.attributes.reusable,
                    'text-info': code.attributes.reusable,
                  }"
                >
                  {{
                    code.attributes.reusable
                      ? $t("account.reusable", "Reusable")
                      : code.attributes.used
                      ? $t("account.used", "Used")
                      : $t("account.available", "Available")
                  }}
                </span>
              </div>

              <div class="promocode-row__controls">
                <button
                  v-if="!code.attributes.used"
                  :disabled="loading"
                  type="button"
                  class="bttn-round btn-remove-promocode"
                  name="button"
                  @click="editModal(code)"
                >
                  <b-icon-pencil color="currentColor" />
                </button>
                <button
                  :disabled="loading"
                  type="button"
                  class="bttn-round btn-remove-promocode"
                  name="button"
                  @click="deletePromoCode(code)"
                >
                  <b-icon-trash color="currentColor" />
                </button>
              </div>
            </div>
          </div>
          <div v-else class="promocode-wrap promocode-empty">
            {{ $t("account.no_promo_codes", "No promo codes") }}
          </div>
        </b-overlay>

        <!-- Generate Promo Codes Modal -->
        <b-modal id="promoCodesModal" @hidden="resetForm">
          <template #modal-header>
            <h2 v-if="modalMode === 'create'">
              {{ $t("account.generate_promo_codes", "Generate promo codes") }}
            </h2>
            <h2 v-if="modalMode === 'edit'">
              {{ $t("account.edit_promo_codes", "Edit promo code") }}
            </h2>
          </template>
          <template #modal-footer>
            <div>
              <button type="button" class="bttn bttn--lg bttn--blue" @click="hideModal()">
                {{ $t("account.cancel") }}
              </button>
              <button
                :disabled="loading"
                type="submit"
                class="bttn bttn--lg bttn--orange"
                form="promoCodesModalForm"
              >
                {{ $t("account.save") }}
              </button>
            </div>
          </template>

          <b-form id="promoCodesModalForm" @submit.prevent="onSubmit">
            <b-form-group
              v-if="modalMode === 'create'"
              :label="$t('account.number_of_codes', 'Number of codes')"
              label-for="numberOfCodes"
            >
              <b-form-input
                id="numberOfCodes"
                v-model="form.numberOfCodes"
                type="number"
                min="1"
                max="10"
                required
              ></b-form-input>
            </b-form-group>
            <b-form-group
              v-if="modalMode === 'edit'"
              :label="$t('account.promo_codes_code', 'Code')"
              label-for="code"
            >
              <b-form-input id="code" v-model="form.code" type="text" required></b-form-input>
            </b-form-group>
            <b-form-group
              :label="$t('account.discount_percentage', 'Discount percentage')"
              label-for="discountPercentage"
            >
              <b-form-input
                id="discountPercentage"
                v-model="form.discountPercentage"
                type="number"
                min="0"
                max="100"
                required
              ></b-form-input>
            </b-form-group>
            <b-form-group label-for="reusable">
              <b-form-checkbox
                id="reusable"
                v-model="form.reusable"
                name="reusable"
                :value="true"
                :unchecked-value="false"
              >
                {{ $t("account.promo_code_reusable", "Reusable?") }}
              </b-form-checkbox>
            </b-form-group>
          </b-form>
        </b-modal>

        <button
          type="button"
          class="bttn bttn--sm bttn--blue tw-me-auto tw-mt-7"
          @click="hide"
          name="button"
        >
          {{ $t("edit_campaign.setting_close", "Close") }}
        </button>
      </div>
    </template>
  </SettingModal>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import urls from "@/constants/urls.js";
import { notificationsMixin } from "@/mixins";
import SettingModal from "@/views/campaigns/campaign-settings/settingModal.vue";

export default {
  mixins: [notificationsMixin],

  components: {
    SettingModal,
  },

  props: {
    baseSettingsItem: {
      type: Object,
      default: () => null,
    },
  },

  data() {
    return {
      loading: false,
      promoCodeList: [],
      modalMode: "create",
      editPromoCode: null,
      form: {
        numberOfCodes: 1,
        discountPercentage: 100,
        reusable: false,
        code: "",
      },
    };
  },

  created() {
    this.fetchPromoCodes();
  },

  computed: {
    ...mapState({
      orgId: () => localStorage.getItem("orgId"),
      campaignId: (state) => state.campaign.campaign.data.id,
    }),
  },

  methods: {
    fetchPromoCodes() {
      this.loading = true;
      axios
        .get(urls.promoCodeList(this.orgId, this.campaignId))
        .then((response) => {
          this.promoCodeList = response.data.data;
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(this.$_notificationsMixin_handleCatch);
    },

    showModal() {
      this.$bvModal.show("promoCodesModal");
      this.modalMode = "create";
    },

    editModal(promoCode) {
      this.$bvModal.show("promoCodesModal");
      this.modalMode = "edit";
      this.editPromoCode = promoCode;
      this.form.code = promoCode.attributes.code;
      this.form.discountPercentage = promoCode.attributes.discount;
      this.form.reusable = promoCode.attributes.reusable;
    },

    hideModal() {
      this.$bvModal.hide("promoCodesModal");
    },

    onSubmit() {
      if (this.modalMode === "create") {
        this.createPromoCode();
      } else if (this.modalMode === "edit") {
        this.putPromoCode(this.editPromoCode);
      }
    },

    putPromoCode(promoCode) {
      const payload = {
        data: {
          id: promoCode.id,
          type: "promo_codes_put_request",
          attributes: {
            code: this.form.code,
            discount: parseFloat(this.form.discountPercentage),
            reusable: this.form.reusable,
          },
        },
      };

      this.loading = true;
      return axios
        .put(urls.promoCodeById(this.orgId, this.campaignId, promoCode.id), payload)
        .then(() => {
          this.hideModal();
          this.fetchPromoCodes();
          this.$_notificationsMixin_makeToast(
            "Success",
            this.$t("account.promo_codes_edit", "Promo code edited!")
          );
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(this.$_notificationsMixin_handleCatch);
    },

    createPromoCode() {
      const payload = {
        data: {
          id: "0",
          type: "promo_codes_generate_request",
          attributes: {
            count: parseFloat(this.form.numberOfCodes),
            discount: parseFloat(this.form.discountPercentage),
            reusable: this.form.reusable,
          },
        },
      };

      this.loading = true;
      return axios
        .post(urls.promoCode(this.orgId, this.campaignId), payload)
        .then(() => {
          this.hideModal();
          this.fetchPromoCodes();
          this.$_notificationsMixin_makeToast(
            "Success",
            this.$t("account.promo_codes_generated", "Promo codes generated")
          );
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(this.$_notificationsMixin_handleCatch);
    },

    deletePromoCode(code) {
      this.$dialog
        .confirm(this.$t("account.confirm_delete_promo_code", "Delete promo code?"), {
          okText: this.$t("account.confirm_delete", "Delete"),
          cancelText: this.$t("account.confirm_cancel", "Cancel"),
        })
        .then(() => {
          this.loading = true;
          axios
            .delete(urls.promoCodeById(this.orgId, this.campaignId, code.id))
            .then(() => {
              this.fetchPromoCodes();
              this.$_notificationsMixin_makeToast("Success", this.$t("account.promo_code_deleted"));
            })
            .finally(() => {
              this.loading = false;
            })
            .catch(this.$_notificationsMixin_handleCatch);
        });
    },

    resetForm() {
      this.form.numberOfCodes = 1;
      this.form.discountPercentage = 100;
      this.form.reusable = false;
      this.form.code = "";
      this.modalMode = "create";
      this.editPromoCode = null;
    },

    copy(code) {
      navigator.clipboard.writeText(code.attributes.code);
      this.$bvToast.toast(this.$t("account.promo_code_copied", "Promo code copied"), {
        title: this.$t("account.promo_code_copied", "Promo code copied"),
        variant: "success",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.promocode-wrap {
  margin-top: 20px;
}

.promocode-row {
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid rgba(32, 67, 78, 0.15);

  &__code {
    flex: 2;
    font-weight: bold;
    display: flex;
    gap: 10px;
    align-items: center;
  }

  &__discount {
    flex: 1;
  }

  &__status {
    flex: 1;
  }

  &__controls {
    flex: 0 0 auto;
    display: flex;
    gap: 10px;
  }
}

.promocode-empty {
  text-align: center;
  padding: 20px;
  color: #666;
}
</style>
